import { useEffect } from "react";
import { useBlocker } from "react-router";
import { useTranslation } from "@ncp/i18n";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@ncp/ui";

/**
 * Blocks in-app navigation and tab close while an editor holds unsaved changes. Render `<UnsavedChangesDialog blocker={...} />`
 * from the returned value so the operator can stay or discard.
 */
export function useUnsavedChanges(dirty: boolean) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome requires returnValue to be set.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  return blocker;
}

export function UnsavedChangesDialog({ blocker }: { blocker: ReturnType<typeof useBlocker> }) {
  const { t } = useTranslation();
  const open = blocker.state === "blocked";
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && blocker.state === "blocked") blocker.reset(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{t("admin:shell.unsavedTitle")}</DialogTitle>
          <DialogDescription>{t("admin:shell.unsavedBody")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => blocker.state === "blocked" && blocker.reset()}>{t("admin:shell.stay")}</Button>
          <Button variant="destructive" onClick={() => blocker.state === "blocked" && blocker.proceed()}>{t("admin:shell.discard")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
